import React, { useState, useEffect, useRef } from "react";
import FontPicker from "font-picker-react";
import TabbedPanel from "../../../../components/TabbedPanel/TabbedPanel";
import FormDropDown from "../../../../components/forms/DropDownList/FormDropDown";
import { applyFontPreview, FontPreviewBlock, getCustomFontUrl } from "../../../../helpers/CustomFonts";
import { slideOutPanelControl } from "../SlideoutPanel";
import './FontSelectorContent.scss';

const FontSelectorContent = (props) => {
    const customFonts = props.customFonts || [];
    const campaignId = props.campaignId;
    const [busy, setBusy] = useState(false);

    const isCustomFont = (fontFamily) => {
        if (customFonts) {
            const font = customFonts.find(f => f.fontFamily === fontFamily);
            return font ? true : false;
        }
        return false;
    }

    const [activeFontFamily, setActiveFontFamily] = useState(props.currentFont || 'Roboto');
    const [activeGoogleFont, setActiveGoogleFont] = useState(isCustomFont(props.currentFont) ? 'Roboto' : props.currentFont);
    const [activeCustomFont, setActiveCustomFont] = useState(isCustomFont(props.currentFont) ? props.currentFont : (customFonts?.[0]?.fontFamily ?? ''));
    
    const initialTab = isCustomFont(props.currentFont) ? 'custom' : 'google';
    const [currentTab, setCurrentTab] = useState(initialTab);

    const tabData = [
        { label: 'Google Fonts', id: 'google' },
        { label: 'Custom Fonts', id: 'custom', hidden: (!customFonts || customFonts.length === 0) }
    ];

    const customFontsDropdown = customFonts.map ? customFonts.map(f => ({ label: f.fontFamily, value: f.fontFamily, fontFile: f.fontFilename, fontData: f })) : [];
    // Show currently active custom font as selected, or -1 if Google font is active
    const currentCustomFontIndex = isCustomFont(activeFontFamily) ? customFontsDropdown.findIndex(f => f.value === activeFontFamily) : -1;

    const applyCustomFontToPreview = (fontData = null) => {
        if (!customFonts || customFonts.length === 0) {
            return;
        }

        if (fontData === null) {
            fontData = customFontsDropdown[currentCustomFontIndex];
        }
        applyFontPreview(fontData.value, getCustomFontUrl(fontData.fontData || fontData.fontFile, campaignId));
    }

    // Update parent with font changes for preview
    useEffect(() => {
        if (props.isOpenCanMakeChanges && props.onFontChanged && activeFontFamily && activeFontFamily !== props.currentFont) {
            props.onFontChanged(activeFontFamily);
        }
    }, [activeFontFamily, props.isOpenCanMakeChanges]);

    // Handle panel close/confirm
    useEffect(() => {
        if (!props.isOpen && props.onFontSelected && activeFontFamily && activeFontFamily !== props.currentFont) {
            props.onFontSelected(activeFontFamily);
        }
    }, [props.isOpen]);

    useEffect(() => {
        if (currentTab === 'custom') {
            applyCustomFontToPreview();
        }
    }, []);

    const handleTabSwitch = (tabId, tabData) => {
        setCurrentTab(tabData.id);
        // Don't auto-select fonts when switching tabs
        // Let user explicitly choose from each tab
    };

    const fontSearchTermRef = useRef('');
    const fontSearchTimerRef = useRef(null);
    const fontSearchResetOnNextRef = useRef(false);

    const getFontScore = (label, term) => {
        const l = label.toLowerCase();
        const t = term.toLowerCase();
        if (l === t) return 100000;                                           // exact match
        if (l.startsWith(t)) return 10000;                                    // start of full name
        if (l.split(' ').slice(1).some(w => w.startsWith(t))) return 1000;   // start of a later word
        if (l.includes(t)) return Math.round((t.length / l.length) * 100);   // substring: scored by % of name matched (1-99)
        return 0;
    };

    const applyFontDomFilter = (term) => {
        const ul = document.querySelector('#font-picker ul');
        if (!ul) return;
        const items = Array.from(ul.querySelectorAll('li'));
        if (!term) {
            items.forEach(li => { li.style.display = ''; });
            // Restore original document order
            items.sort((a, b) => (a.dataset.fpOrigOrder | 0) - (b.dataset.fpOrigOrder | 0));
            items.forEach(li => ul.appendChild(li));
            return;
        }
        // Stamp original order once
        items.forEach((li, i) => { if (!li.dataset.fpOrigOrder) li.dataset.fpOrigOrder = i; });
        const scored = items.map(li => {
            const name = li.querySelector('button')?.textContent?.trim() || '';
            return { li, score: getFontScore(name, term) };
        });
        scored.sort((a, b) => {
            if (b.score !== a.score) return b.score - a.score;
            const na = a.li.querySelector('button')?.textContent?.trim() || '';
            const nb = b.li.querySelector('button')?.textContent?.trim() || '';
            return na.localeCompare(nb);
        });
        scored.forEach(({ li, score }) => {
            li.style.display = score > 0 ? '' : 'none';
            ul.appendChild(li); // reorder by appending in sorted order
        });
        // Scroll the first visible match into view
        const first = scored.find(({ score }) => score > 0);
        if (first) first.li.scrollIntoView({ block: 'nearest' });
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            const picker = document.querySelector('#font-picker');
            if (!picker || !picker.classList.contains('expanded')) return;
            if (e.key === 'Backspace' || e.key === 'Delete') {
                e.stopPropagation();
                e.preventDefault();
                // Clear search entirely
                fontSearchTermRef.current = '';
                fontSearchResetOnNextRef.current = false;
                if (fontSearchTimerRef.current) clearTimeout(fontSearchTimerRef.current);
            } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.stopPropagation();
                e.preventDefault();
                // If paused long enough, start fresh
                if (fontSearchResetOnNextRef.current) {
                    fontSearchTermRef.current = '';
                    fontSearchResetOnNextRef.current = false;
                }
                fontSearchTermRef.current += e.key;
                // After a pause, mark for reset on next keypress (but keep current filter visible)
                if (fontSearchTimerRef.current) clearTimeout(fontSearchTimerRef.current);
                fontSearchTimerRef.current = setTimeout(() => {
                    fontSearchResetOnNextRef.current = true;
                }, 1000);
            } else {
                return;
            }
            applyFontDomFilter(fontSearchTermRef.current);
        };
        // Capture phase: runs before FontPicker's own keydown handlers,
        // so stopPropagation prevents FontPicker from closing on keypresses.
        document.addEventListener('keydown', handleKeyDown, true);
        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
            if (fontSearchTimerRef.current) clearTimeout(fontSearchTimerRef.current);
        };
    }, []);

    const handleGoogleFontChange = (nextFont) => {
        setActiveFontFamily(nextFont.family);
        setActiveGoogleFont(nextFont.family);
        if (props.onFontSelected) {
            props.onFontSelected(nextFont.family);
        }
    };

    const handleCustomFontChange = (e, id, item) => {
        setActiveFontFamily(item.value); 
        setActiveCustomFont(item.value);
        applyCustomFontToPreview(item);
        // Apply font selection immediately
        if (props.onFontSelected) {
            props.onFontSelected(item.value);
        }
    };

    return (
        <div className="font-selector-content">
            <TabbedPanel
                tabs={tabData}
                switchTabCallback={handleTabSwitch}
                initialTab={tabData.findIndex(t => t.id === initialTab)}
                tabWidth="50%"
                tabStyle={2}
            >
                <>
                    {currentTab === 'google' && (
                        <div className="fl-column font-selector-tab-content">
                            <FontPicker
                                apiKey="AIzaSyCK-FT-TdFUvWTWBQ_hxsoU_HS6GHeytWg"
                                activeFontFamily={isCustomFont(activeFontFamily) ? 'Select a Google font' : activeFontFamily}
                                limit={250}
                                onChange={handleGoogleFontChange}
                            />
                            <FontPreviewBlock className="apply-font" />
                        </div>
                    )}
                    {currentTab === 'custom' && (
                        <div className="fl-column font-selector-tab-content">
                            <FormDropDown
                                id="customFonts"
                                label="Custom Fonts"
                                showLabel={false}
                                showErrors={false}
                                currentIndex={currentCustomFontIndex >= 0 ? currentCustomFontIndex : 0}
                                selectFunc={handleCustomFontChange}
                                items={currentCustomFontIndex >= 0 ? customFontsDropdown : [{ label: "Select a custom font", value: "", disabled: true }, ...customFontsDropdown]}
                            />
                            <FontPreviewBlock className="apply-custom-font" />
                        </div>
                    )}
                </>
            </TabbedPanel>
        </div>
    );
}

export default FontSelectorContent;